// 11 §4.2 `terrain` + `props` layers. The forge decides what each square *is*; the view-tile
// ruleset (game/tiling, #166 / 12 §8 T9) decides what it looks like: kit fills, Oryx wall pieces
// by connected-wall mask, liquid shorelines, ground lips, shadows and decals, all deterministic
// by (kit, x, y). Props drawn here as vectors carry what the kit has no art for yet: exit marks,
// hazards, traps, objective tiles (board-camera space, so they scale with the tiles). Enemy spawn
// tiles are deliberately left unmarked.
//
// Mark vocabulary (also in the gallery caption and the HUD legend):
//   exit        gold wash + double chevron pointing off the board (the party enters and leaves here;
//               dungeon kits add their stairs sprite underneath)
//   objective   gold ring
//   hazard      tinted square (fire: triangle), trap: red cross
import Phaser from 'phaser';
import { parseSeason, seasonHour } from '../weather/season';
import type { Snapshot, TileData } from '../../core/sim/types';
import type { AtlasManifest } from '../../config';
import { atlasForFrame } from '../assets';
import { sameValue } from './sameValue';
import { cellHash, inferKit, kitFor, oryxKeyOf, tileBoard, type Kit, type TiledBoard } from '../tiling';
import { BakedChunks, type Stamp } from './BakedChunks';
import { EncounterTerrain, oryxForced } from '../encounter/EncounterTerrain';
import { chooseSet, setIdsAvailable, type SetWant } from '../encounter/sets';
import type { ShownBoard } from '../encounter/prepare';

/**
 * docs/186 EA5: what a renderer needs to draw the commissioned art: the layer above the light map for the lit
 * pixels, the units layer the standing images sort in, what the scene knows of the fight (biome, season, hour,
 * or a forced time of day; undefined keeps the Oryx look), and what to do when a set has loaded.
 */
export interface ArtHost {
  lit: Phaser.GameObjects.Layer;
  units: Phaser.GameObjects.Layer;
  want: (board: Snapshot['board'], kit: string) => Omit<SetWant, 'kit'> | undefined;
  onReady: () => void;
}

/** `?kit=<key>` overrides the kit for every board (art review); otherwise the snapshot's kit or a guess. */
export function kitOverride(): string | undefined {
  try { return new URLSearchParams(window.location.search).get('kit') ?? undefined; } catch { return undefined; }
}

/** Mark colours, shared with the HUD legend (`tokens.css` --fk-mark-*). */
export const MARKS = { exit: 0xffd166, objective: 0xffd166 } as const;
/** The dark line drawn under the marks so they read on sand and snow. */
const MARK_KEYLINE = 0x1a1006;
/** The rock of a terrace's ledge (docs/66 RM15): its face, lit top, seams and outline. */
const LEDGE = { face: 0x8f6552, light: 0xc39578, seam: 0x63412f, line: 0x2e1d15 } as const;

import { edgeDirOn, heightOf, type Dir } from './edges';
export { edgeDir, edgeDirOn, heightOf } from './edges';

export class TileRenderer {
  /** The ground art and terraces, baked (nothing on them moves between draws). */
  private baked: BakedChunks;
  /** What the bake was made from: a redraw of the same ground keeps it. */
  private bakedKey = '';
  private props?: Phaser.GameObjects.Graphics;
  /** What the ground was last drawn from (a copy). */
  private drawn?: { board: Ground; kit: string; marks: boolean; art: string };
  private terraces?: Phaser.GameObjects.Graphics;
  private timer?: Phaser.Time.TimerEvent;
  private phase = 0;
  /** The last tiling, for tests and the gallery caption (the Oryx look only: undefined while the new art shows). */
  tiled?: TiledBoard;
  /** docs/186: the commissioned art, when the scene draws it. */
  readonly art?: EncounterTerrain;
  private readonly host?: ArtHost;

  constructor(private readonly scene: Phaser.Scene, private readonly layer: Phaser.GameObjects.Layer, private readonly propsLayer: Phaser.GameObjects.Layer, private readonly manifest: AtlasManifest, host?: ArtHost) {
    this.baked = new BakedChunks(scene, layer);
    if (host) {
      this.host = host;
      this.art = new EncounterTerrain(scene, { ground: layer, lit: host.lit, units: host.units }, host.onReady);
    }
    this.timer = scene.time.addEvent({ delay: 650, loop: true, callback: () => this.tick() });
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => { this.timer?.remove(); this.timer = undefined; });
  }

  /** Which kit a board draws with: explicit override → the board's own key → a guess from its terrain mix. */
  kitForBoard(board: Snapshot['board']): Kit { return kitFor(kitOverride() ?? oryxKeyOf(board) ?? inferKit(board)); }

  /** The painted board on show (undefined: the Oryx look). */
  get painted(): ShownBoard | undefined { return this.art?.painted; }
  /** The set the board is drawn in ('' for the Oryx look), for keys that must change when the art lands. */
  get artId(): string { return this.art?.set?.id ?? ''; }
  /** Board px the standing art rises above row 0, for the camera. */
  get headroom(): number { return this.art?.headroom ?? 0; }

  /** `board` painted in the art, if its set has loaded and the painting is done (starting both if not); undefined keeps the Oryx look. */
  private artFor(board: Snapshot['board'], kit: string) {
    if (!this.art || !this.host || oryxForced()) return undefined;
    const asked = this.host.want(board, kit);
    if (!asked) return undefined;
    // night by the hour the light is drawn at (a summer evening is still light), as the lighting judges it
    const season = parseSeason(asked.season);
    const want = asked.hour !== undefined && season ? { ...asked, hour: seasonHour(asked.hour, season) } : asked;
    // the board's own kit name first (the art has kits the Oryx look draws as another), then the Oryx kit's
    const raw = kitOverride() ?? board.kit;
    const choice = (raw && raw !== kit ? chooseSet({ kit: raw, ...want }, setIdsAvailable()) : undefined) ?? chooseSet({ kit, ...want }, setIdsAvailable());
    if (!choice) return undefined;
    const ready = this.art.ready(choice.id, board);
    if (!ready && !this.art.hasFailed(choice.id)) this.waiting = { id: choice.id, since: this.waiting?.id === choice.id ? this.waiting.since : performance.now() };
    else this.waiting = undefined;
    return ready;
  }

  /** The set the board waits for, and since when (docs/186: the board holds dark rather than flash the Oryx look). */
  private waiting?: { id: string; since: number };
  /** How long a board holds dark for its art before it falls back to the Oryx look. */
  private static readonly HOLD_MS = 4000;

  draw(snapshot: Pick<Snapshot, 'board'>, marks = true): void {
    const kit = this.kitForBoard(snapshot.board);
    const art = this.artFor(snapshot.board, kit.key);
    // while the art paints (about a second for a large crawl) the board holds dark under its marks, rather
    // than show the old tiles and swap; a set that takes too long falls back to them (docs/186)
    const hold = !art && !!this.waiting && performance.now() - this.waiting.since < TileRenderer.HOLD_MS;
    if (hold && this.drawn?.art !== 'hold') this.scene.time.delayedCall(TileRenderer.HOLD_MS + 50, () => { if (this.drawn?.art === 'hold') this.host?.onReady(); });
    // a new painting (the board changed, or its set) redraws, as a new set does
    const artId = art?.key ?? (hold ? 'hold' : '');
    // docs/147 PG7: most snapshots (every turn, every pulse) bring the same ground; tiling a crawl
    // for twenty again and drawing its marks again took ~45 ms of every turn. The same ground keeps
    // its tiling, bake and marks; the marks only move to the top of the props layer, where a fresh
    // drawing would have gone. (The board is compared by value against a copy: a live snapshot is
    // changed in place as the playback lands.)
    const same = this.props && this.drawn && this.drawn.kit === kit.key && this.drawn.marks === marks && this.drawn.art === artId && sameValue(this.drawn.board, groundOf(snapshot.board));
    if (same) {
      this.propsLayer.bringToTop(this.props!);
      this.baked.setPhase(0);
      return;
    }
    this.drawn = { board: structuredClone(groundOf(snapshot.board)), kit: kit.key, marks, art: artId };
    this.props?.destroy();
    const { w, h, tiles } = snapshot.board;
    const cell = this.manifest.cell;
    const g = this.scene.add.graphics();
    this.props = g;
    this.propsLayer.add(g);
    if (art) {
      // docs/186: the commissioned art: painted ground, lit pixels and standing images; the terraces over the
      // ground and the vector marks over everything, as on the Oryx look
      this.tiled = undefined;
      const painted = this.art!.show(art);
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const t = tiles[y * w + x];
        if (t && marks) this.drawProps(g, t, x * cell, y * cell, cell, edgeDirOn(snapshot.board, x, y));
      }
      this.terraces?.destroy();
      this.terraces = this.scene.make.graphics({}, false);
      // a kit that draws its own ledges and cliffs (docs/164 ET7) leaves the vector ones off
      // docs/186, performance: the squares the terraces draw on, so only the chunks holding any are baked (a chunk
      // is a framebuffer: forty of them for a crawl's few ledges took ~200 ms as the art landed)
      const touched = new Uint8Array(w * h);
      if (!painted.ledges) this.drawTerraces(this.terraces, snapshot.board, cell, touched);
      const any = (this.terraces as unknown as { commandBuffer: unknown[] }).commandBuffer.length > 0;
      this.bakedKey = '';
      const holds = (x0: number, y0: number, cw: number, ch: number): boolean => {
        for (let y = Math.max(0, Math.floor(y0 / cell)); y <= Math.min(h - 1, Math.floor((y0 + ch - 1) / cell)); y++)
          for (let x = Math.max(0, Math.floor(x0 / cell)); x <= Math.min(w - 1, Math.floor((x0 + cw - 1) / cell)); x++) if (touched[y * w + x]) return true;
        return false;
      };
      if (any) this.baked.bake(w * cell, h * cell, [], [this.terraces], holds);
      else this.baked.clear();
      return;
    }
    this.art?.clear();
    if (hold) {
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const t = tiles[y * w + x];
        if (t && marks) this.drawProps(g, t, x * cell, y * cell, cell, edgeDirOn(snapshot.board, x, y));
      }
      this.tiled = undefined;
      this.bakedKey = '';
      this.baked.clear();
      return;
    }
    const hasWorld = this.scene.textures.exists('world');
    const tiled = hasWorld ? tileBoard(snapshot.board, kit) : undefined;
    this.tiled = tiled;
    // the ground's images, in the order they were drawn, for the bake
    const stamps: Stamp[] = [];
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const t = tiles[y * w + x];
        if (!t) continue;
        const view = tiled?.cells[y * w + x];
        if (view) {
          for (const f of [...view.ground, ...view.overlay, ...view.props]) {
            const key = atlasForFrame(f.frame);
            if (!this.scene.textures.get(key).has(f.frame)) continue;
            const alt = f.anim && f.anim.length > 1 ? f.anim[1]! : undefined;
            stamps.push({ key, frame: f.frame, x: x * cell, y: y * cell, ...(alt && this.scene.textures.get(atlasForFrame(alt)).has(alt) ? { alt: { key: atlasForFrame(alt), frame: alt } } : {}) });
          }
        } else {
          // dev atlas fallback (no Oryx world sheet): flat role squares
          const role = t.terrain === 'Wall' ? 'wall' : t.terrain === 'Water' ? 'water' : t.terrain === 'Door' ? 'door' : 'floor';
          const r = this.manifest.roles[role] ?? this.manifest.roles['floor'];
          if (r) stamps.push({ key: r.key, frame: String(r.frame), x: x * cell, y: y * cell });
        }
        if (marks) this.drawProps(g, t, x * cell, y * cell, cell, edgeDirOn(snapshot.board, x, y));
      }
    }
    // the terraces go over the ground art, under the light (night darkens them as it does the ground)
    const key = `${w}x${h}|${stamps.map((st) => `${st.frame}${st.alt ? `~${st.alt.frame}` : ''}`).join(',')}|${tiles.map((t) => (t ? `${t.terrain}${t.elev}` : '')).join(',')}`;
    if (key !== this.bakedKey) {
      this.bakedKey = key;
      // (kept off the display list, and kept: a lost WebGL context bakes again from it)
      this.terraces?.destroy();
      this.terraces = this.scene.make.graphics({}, false);
      this.drawTerraces(this.terraces, snapshot.board, cell);
      this.baked.bake(w * cell, h * cell, stamps, [this.terraces]);
    }
    // a fresh bake (or a kept one) shows the tiles' own frames until the next beat, as new images did
    this.baked.setPhase(0);
  }

  /**
   * docs/66 RM15: terraces drawn as rocky ledges, the way top-down RPGs draw a step up: the
   * ground is the same above and below, and a strip of rounded rocks runs along each step — a
   * face under a south-facing step (taller for two levels), a narrow side under an east or west
   * one, a thin lip on a north one (the sides and lips kept faint for one level, so long steps do
   * not grid the board). A cliff the forge made unwalkable (a `Chasm` kept at its height; a pit
   * is level) is a whole square of that rock. Everything is drawn from the lower square looking
   * at its higher neighbours, the rocks varied by hash(x, y) so no run repeats. High ground
   * gives +1 to blows on lower ground and a missile one square more.
   */
  private drawTerraces(g: Phaser.GameObjects.Graphics, board: Pick<Snapshot, 'board'>['board'], c: number, touched?: Uint8Array): void {
    const { w, h, tiles } = board;
    const px = c / 24; // one art pixel
    const at = (x: number, y: number): TileData | undefined => (x < 0 || y < 0 || x >= w || y >= h ? undefined : tiles[y * w + x]);
    const cliff = (t: TileData | undefined): boolean => t?.terrain === 'Chasm' && t.elev > 0;
    // the height a square stands at for its edges: a cliff at its own, a pit or the void none
    const level = (t: TileData | undefined): number | undefined => (!t || t.terrain === 'Void' || (t.terrain === 'Chasm' && !cliff(t)) ? undefined : heightOf(t));
    const fill = (colour: number, x: number, y: number, rw: number, rh: number, alpha = 1): void => {
      g.fillStyle(colour, alpha); g.fillRect(x, y, rw, rh);
      if (touched) for (let ty = Math.max(0, Math.floor(y / c)); ty <= Math.min(h - 1, Math.floor((y + rh - 1) / c)); ty++) for (let tx = Math.max(0, Math.floor(x / c)); tx <= Math.min(w - 1, Math.floor((x + rw - 1) / c)); tx++) touched[ty * w + tx] = 1;
    };
    /** One square's width of rounded rocks, `d` art px tall from `top`: widths 5–9 px, some set a pixel lower. */
    const rocks = (ox: number, top: number, d: number, x: number, y: number, salt: string): void => {
      for (let k = 0, from = 0; from < 24; k++) {
        const r = cellHash('ledge', x, y, salt + k);
        let bw = 5 + (r % 5);
        if (24 - (from + bw) < 4) bw = 24 - from; // no sliver at the square's end
        const bx = ox + from * px, rw = bw * px, t0 = top + ((r >>> 4) % 3 === 0 ? px : 0), rh = top + d * px - t0;
        fill(LEDGE.face, bx + px, t0, rw - 2 * px, px); // the crown, corners cut round
        fill(LEDGE.face, bx, t0 + px, rw, rh - px);
        fill(LEDGE.light, bx + 2 * px, t0, rw - 4 * px, px);
        fill(LEDGE.light, bx + px, t0 + px, 2 * px, px);
        fill(0x000000, bx, t0 + Math.round(rh * 0.6 / px) * px, rw, rh - Math.round(rh * 0.6 / px) * px, 0.18);
        fill(LEDGE.seam, bx + rw - px, t0 + 2 * px, px, rh - 2 * px);
        from += bw;
      }
    };
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const t = at(x, y);
        const e = level(t);
        if (e === undefined) continue;
        const ox = x * c, oy = y * c;
        if (cliff(t)) {
          // a wall of rock: three courses of rocks on a dark bed, darker at its foot
          fill(LEDGE.seam, ox, oy, c, c);
          for (let r = 0; r < 3; r++) rocks(ox, oy + r * 8 * px, 8, x, y, `cliff${r}.`);
          fill(0x000000, ox, oy + 16 * px, c, 8 * px, 0.14);
          fill(LEDGE.line, ox, oy + c - px, c, px);
          continue;
        }
        const higher = (dx: number, dy: number): number => {
          const n = at(x + dx, y + dy);
          const ne = level(n);
          return ne === undefined || ne <= e ? 0 : Math.min(ne - e, 2);
        };
        const north = higher(0, -1), west = higher(-1, 0), east = higher(1, 0), south = higher(0, 1);
        if (north && cliff(at(x, y - 1))) {
          // the foot of a cliff square: only its shadow on this ground
          fill(0x000000, ox, oy, c, 2 * px, 0.25);
        } else if (north) {
          // a face under the step, reaching up over the higher square's foot
          const d = north >= 2 ? 12 : 8, top = oy - 2 * px;
          rocks(ox, top, d, x, y, 'face');
          fill(LEDGE.line, ox, top + (d - 1) * px, c, px);
          fill(0x000000, ox, top + d * px, c, 2 * px, 0.22);
        }
        for (const [rise, onLeft] of [[west, true], [east, false]] as const) {
          if (!rise) continue;
          // the step's side: lit against the higher ground, outlined against this one
          if (rise < 2) {
            const sx = onLeft ? ox : ox + c - 2 * px;
            fill(LEDGE.light, onLeft ? sx : sx + px, oy, px, c, 0.55);
            fill(LEDGE.line, onLeft ? sx + px : sx, oy, px, c, 0.5);
            continue;
          }
          const d = 4 * px, sx = onLeft ? ox : ox + c - d;
          fill(LEDGE.face, sx, oy, d, c);
          fill(LEDGE.light, onLeft ? sx : sx + d - px, oy, px, c);
          const r = cellHash('ledge', x, y, onLeft ? 'west' : 'east');
          fill(LEDGE.seam, sx, oy + (5 + (r % 6)) * px, d, px);
          fill(LEDGE.seam, sx, oy + (15 + ((r >>> 8) % 6)) * px, d, px);
          fill(LEDGE.line, onLeft ? sx + d - px : sx, oy, px, c);
        }
        if (south) {
          // a north-facing step shows only its lip
          const d = south >= 2 ? 3 : 2;
          fill(LEDGE.face, ox, oy + c - d * px, c, d * px, south >= 2 ? 1 : 0.6);
          fill(LEDGE.line, ox, oy + c - d * px, c, px, south >= 2 ? 1 : 0.5);
        }
      }
    }
  }

  /** Per frame: the art's tints follow the light (EncounterTerrain.update). */
  update(time: number): void { this.art?.update(time); }

  private tick(): void {
    this.phase ^= 1;
    this.baked.setPhase(this.phase);
  }

  private drawProps(g: Phaser.GameObjects.Graphics, t: TileData, ox: number, oy: number, c: number, out: Dir): void {
    const m = Math.max(2, Math.floor(c / 6));
    // enemy spawn tiles carry no mark: players don't need to see where foes arrive
    if (t.exit) {
      // exit: gold wash and a double chevron pointing off the board
      g.fillStyle(MARKS.exit, 0.18); g.fillRect(ox + 1, oy + 1, c - 2, c - 2);
      const cx = ox + c / 2, cy = oy + c / 2, a = Math.max(3, Math.floor(c / 4)), gap = Math.max(2, Math.floor(c / 6));
      const chevron = (sx: number, sy: number): void => {
        // a "<" whose tip is at (sx, sy), opening away from `out`
        if (out === 'w' || out === 'e') { const d = out === 'w' ? 1 : -1; g.lineBetween(sx + d * a, sy - a, sx, sy); g.lineBetween(sx, sy, sx + d * a, sy + a); }
        else { const d = out === 'n' ? 1 : -1; g.lineBetween(sx - a, sy + d * a, sx, sy); g.lineBetween(sx, sy, sx + a, sy + d * a); }
      };
      const both = (): void => {
        if (out === 'w') { chevron(cx - gap - 1, cy); chevron(cx + gap + 1, cy); }
        else if (out === 'e') { chevron(cx + gap + 1, cy); chevron(cx - gap - 1, cy); }
        else if (out === 'n') { chevron(cx, cy - gap - 1); chevron(cx, cy + gap + 1); }
        else { chevron(cx, cy + gap + 1); chevron(cx, cy - gap - 1); }
      };
      g.lineStyle(3, MARK_KEYLINE, 0.5); both();
      g.lineStyle(1, MARKS.exit, 0.95); both();
    }
    if (t.objective) { g.lineStyle(1, MARKS.objective, 1); g.strokeCircle(ox + c / 2, oy + c / 2, c / 2 - m); }
    if (t.hazard) {
      const fire = t.hazard === 'Fire' || t.hazard === 'Burning';
      const colour = fire ? 0xff7a1a : t.hazard === 'DeepWater' ? 0x3d85ff : 0xcccccc;
      g.fillStyle(colour, 0.35); g.fillRect(ox + 1, oy + 1, c - 2, c - 2);
      g.lineStyle(1, colour, 0.9);
      if (fire) g.strokeTriangle(ox + c / 2, oy + m, ox + c - m * 2, oy + c - m, ox + m * 2, oy + c - m);
      else g.strokeRect(ox + m, oy + m, c - m * 2, c - m * 2);
    }
    if (t.trap) {
      g.lineStyle(1, 0xff4d4d, 0.95);
      g.lineBetween(ox + m * 2, oy + m * 2, ox + c - m * 2, oy + c - m * 2); g.lineBetween(ox + c - m * 2, oy + m * 2, ox + m * 2, oy + c - m * 2);
    }
  }
}

/** What the ground is drawn from: the board's size, seed and squares, and the place's country and look (docs/186 EA13–EA14). */
type Ground = Pick<Snapshot['board'], 'w' | 'h' | 'seed' | 'tiles' | 'country' | 'look'>;
const groundOf = (b: Snapshot['board']): Ground => ({ w: b.w, h: b.h, seed: b.seed, tiles: b.tiles, ...(b.country ? { country: b.country } : {}), ...(b.look ? { look: b.look } : {}) });
